import { useState, useCallback } from "react";
import mainAxios from "../../../services/mainAxios";
import moment from "moment";
import { getTransformedData } from "../utils/reportConfigs";

const useReportData = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const clearData = useCallback(() => {
    setData(null);
    setError(null);
  }, []);

  const fetchReport = useCallback((config, dateRange, businessUnit, limit, allBuCodes, t, purchaseUnits) => {
    if (!config) return;

    setLoading(true);
    setError(null);
    setData(null);

    const fromDate = moment(dateRange.fromDate).format("YYYY-MM-DD");
    const toDate = moment(dateRange.toDate).format("YYYY-MM-DD");

    // Choose branch vs company endpoint
    const endpoint = businessUnit && config.endpoint
      ? config.endpoint
      : config.companyEndpoint || config.endpoint;

    const requestBody = {};

    // Period-based endpoints (sale/purchase book stats) use period param
    if (config.usesPeriod) {
      requestBody.object = config.object;
      const period = moment(dateRange.fromDate).format("M/YYYY");
      requestBody.params = { period };
      if (businessUnit) {
        requestBody.params.businessUnitCode = businessUnit;
      }
      if (config.hasPurchaseUnit && purchaseUnits?.length > 0) {
        requestBody.params.purchaseUnit = purchaseUnits.join(",");
      }
    } else if (config.noObject) {
      // Endpoints like customers/suppliers don't use an object field
      requestBody.fromDate = fromDate;
      requestBody.toDate = toDate;
      requestBody.params = {
        fiscInvoiceType: "NONCASH",
        limit: limit || 10,
      };
    } else {
      requestBody.object = config.object;
      requestBody.fromDate = fromDate;
      requestBody.toDate = toDate;

      if (config.paramsKey === "buCodes" && allBuCodes?.length > 0) {
        // Daily analytics endpoint needs all buCodes joined
        requestBody.params = { buCodes: businessUnit || allBuCodes.join(",") };
      } else if (config.paramsKey === "businessUnitCode" && businessUnit) {
        requestBody.params = { businessUnitCode: businessUnit };
      }
      if (config.hasPurchaseUnit && purchaseUnits?.length > 0) {
        requestBody.params = { ...(requestBody.params || {}), purchaseUnit: purchaseUnits.join(",") };
      }
    }

    if (config.hasLimit && limit && !config.noObject) {
      requestBody.params = { ...(requestBody.params || {}), limit: String(limit) };
    }

    mainAxios
      .post(endpoint, requestBody)
      .then((res) => {
        if (res?.data?.status) {
          let content = res.data.content ?? [];
          if (config.transformKey && t) {
            const transformed = getTransformedData(config, content, t);
            if (transformed) content = transformed;
          } else if (config.transform) {
            content = config.transform(content);
          }
          setData(content);
        } else {
          setError(res?.data?.message || "Failed to fetch report data");
          setData([]);
        }
        setLoading(false);
      })
      .catch((err) => {
        setError(err?.message || "An error occurred");
        setData([]);
        setLoading(false);
      });
  }, []);

  return { data, loading, error, fetchReport, clearData };
};

export default useReportData;
